/**
 * Visual style.
 *
 * Styles are plain data with no rendering knowledge — the renderer maps them onto canvas
 * state, and the SVG/PDF exporters map the same records onto their own attributes. That
 * is only possible because nothing here is canvas-specific.
 *
 * Note the absence of an `enum`: `erasableSyntaxOnly` forbids them, and const-object +
 * union types serialise directly to JSON with no conversion layer.
 */

export const LineStyle = {
  Solid: 'solid',
  Dashed: 'dashed',
  Dotted: 'dotted',
} as const;
export type LineStyle = (typeof LineStyle)[keyof typeof LineStyle];

export const PointStyle = {
  Dot: 'dot',
  Circle: 'circle',
  Square: 'square',
  Cross: 'cross',
  Plus: 'plus',
} as const;
export type PointStyle = (typeof PointStyle)[keyof typeof PointStyle];

/**
 * Which ends of a line wear an arrowhead.
 *
 * Presentation, beside `pointStyle`, and not part of any construction: no figure is a different
 * figure for having an arrow on it. A segment is already stored `a → b` — `directionOf` reads
 * that order, `vector-components` returns `b − a` from it — so the arrow shows an order the
 * object already has rather than claiming a new one.
 *
 * A **ray** is not configured from here. Its arrow is notation saying what the object *is*, like
 * the ones on the axes, and an object that can be drawn as a different object is not a setting.
 */
export const ArrowEnds = {
  None: 'none',
  Start: 'start',
  End: 'end',
  Both: 'both',
} as const;
export type ArrowEnds = (typeof ArrowEnds)[keyof typeof ArrowEnds];

export const FillStyle = {
  None: 'none',
  Solid: 'solid',
  Hatch: 'hatch',
} as const;
export type FillStyle = (typeof FillStyle)[keyof typeof FillStyle];

/**
 * A colour as a CSS string.
 *
 * Kept as an opaque string rather than a parsed structure: the domain never inspects a
 * colour, and both the canvas renderer and the SVG exporter accept CSS colours
 * directly. Parsing here would add a conversion on every draw call for no benefit.
 */
export type Color = string;

export interface Style {
  readonly stroke: Color;
  readonly strokeWidth: number;
  readonly lineStyle: LineStyle;
  readonly fill: Color;
  readonly fillStyle: FillStyle;
  readonly opacity: number;
  readonly pointStyle: PointStyle;
  readonly pointSize: number;
  /** Which ends carry an arrowhead. `none` on everything until somebody asks for one. */
  readonly arrows: ArrowEnds;
}

/**
 * The stroke width of every line in a figure.
 *
 * ## Why one number and not a default among others
 *
 * Because a geometry figure is a *diagram*, not an engineering drawing: it has no near and far
 * to convey by line thickness, and every line in it belongs to the same construction. Varying
 * the weight tells the reader that some lines matter more, which the figure is in no position
 * to know — the line that matters is whichever one the question is about, and the question is
 * not in the picture.
 *
 * ## Why it lives here rather than with the script language
 *
 * It was defined there first, and the result was a document that could hold two weights at
 * once: an object drawn with the toolbar came out at 1.5 and the same object written in the
 * script panel at 1. Both doors lead to the same drawing, so a figure's appearance depended on
 * which one its author happened to use — and the whole argument for the text-to-figure feature
 * is that the two are interchangeable.
 *
 * One number, in the layer both paths already depend on.
 */
export const FIGURE_WEIGHT = 1;

/**
 * Default style.
 *
 * The stroke is near-black rather than pure black, and the point size is generous:
 * these diagrams are projected onto classroom walls and printed in workbooks, where
 * hairlines disappear.
 */
export const DEFAULT_STYLE: Style = Object.freeze({
  stroke: '#1a1a1a',
  strokeWidth: FIGURE_WEIGHT,
  lineStyle: LineStyle.Solid,
  fill: 'transparent',
  fillStyle: FillStyle.None,
  opacity: 1,
  pointStyle: PointStyle.Dot,
  pointSize: 5,
  arrows: ArrowEnds.None,
});

/**
 * Style for construction helpers — the lines a student draws to *find* something rather
 * than to state it.
 *
 * Distinct by default because "hide construction" is a first-class feature of the
 * product, and it is far more useful when helper geometry already looks like helper
 * geometry.
 */
export const CONSTRUCTION_STYLE: Style = Object.freeze({
  ...DEFAULT_STYLE,
  stroke: '#8a8a99',
  strokeWidth: 1,
  lineStyle: LineStyle.Dashed,
});

export function withStyle(base: Style, overrides: Partial<Style>): Style {
  return { ...base, ...overrides };
}

/**
 * The fields on which every given style agrees.
 *
 * A properties panel showing several selected entities has to distinguish "all of these
 * are red" from "these are different colours". Returning a `Partial` — where an absent
 * field means *mixed* — lets the panel render a blank instead of silently displaying the
 * first entity's value and inviting the user to believe it applies to all of them.
 */
export function commonStyle(styles: readonly Style[]): Partial<Style> {
  const first = styles[0];
  if (first === undefined) return {};
  if (styles.length === 1) return { ...first };

  const shared: Partial<Style> = {};
  for (const key of Object.keys(first) as (keyof Style)[]) {
    const value = first[key];
    if (styles.every((style) => style[key] === value)) {
      // The cast is safe: `key` and `value` come from the same object.
      (shared as Record<string, unknown>)[key] = value;
    }
  }
  return shared;
}

/**
 * A small palette offered by the properties panel.
 *
 * Deliberately short and high-contrast: these diagrams end up on projectors and in
 * printed workbooks, where a subtle pastel is indistinguishable from black.
 */
export const STROKE_PRESETS: readonly Color[] = Object.freeze([
  '#1a1a1a',
  '#1d4ed8',
  '#b91c1c',
  '#0f766e',
  '#7c3aed',
  '#c2410c',
  '#8a8a99',
]);
